import { useEffect, useState } from "react";
import api from "../utils/axios";
import Swal from "sweetalert2";
import formatCurrency from "../helper/formatCurrency";

interface CashbackSummary {
  settings: {
    rate: number;
    is_active: boolean;
    min_loss_threshold: number;
  };
  totals: {
    total_cashback_distributed: string;
    total_net_loss_covered: string;
    total_unique_users_rewarded: string;
    total_payout_events: string;
  };
  recentCycles: Array<{
    cycle_date: string;
    users_count: string;
    total_loss: string;
    total_cashback: string;
  }>;
}

interface CashbackLog {
  id: number;
  user_id: number;
  user_name?: string;
  user_phone?: string;
  cycle_date: string;
  total_bet: string;
  total_win: string;
  net_loss: string;
  cashback_rate: string;
  cashback_amount: string;
  status: string;
  created_at: string;
}

const CashbackSettings = () => {
  const [summary, setSummary] = useState<CashbackSummary | null>(null);
  const [rate, setRate] = useState<number>(5.0);
  const [minLoss, setMinLoss] = useState<number>(10.0);
  const [isActive, setIsActive] = useState<boolean>(true);

  // History state
  const [logs, setLogs] = useState<CashbackLog[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchUid, setSearchUid] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [triggering, setTriggering] = useState<boolean>(false);

  const fetchSummary = async () => {
    try {
      const res = await api.get("/cashback/summary");
      if (res.data?.status) {
        setSummary(res.data);
        if (res.data.settings) {
          setRate(Number(res.data.settings.rate) || 5.0);
          setMinLoss(Number(res.data.settings.min_loss_threshold) || 10.0);
          setIsActive(res.data.settings.is_active);
        }
      }
    } catch (err) {
      console.error("Failed to load cashback summary:", err);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/cashback/history", {
        params: {
          page,
          limit: 15,
          uid: searchUid || undefined,
          cycle_date: selectedDate || undefined,
        },
      });

      if (res.data?.status) {
        setLogs(res.data.data || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to load cashback history:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, searchUid, selectedDate]);

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post("/cashback/settings", {
        rate,
        min_loss_threshold: minLoss,
        is_active: isActive,
      });

      if (res.data?.status) {
        Swal.fire("Saved", "Cashback settings updated successfully.", "success");
        fetchSummary();
      }
    } catch (err: any) {
      Swal.fire("Error", err?.response?.data?.message || "Failed to update settings", "error");
    }
  };

  const handleManualTrigger = async () => {
    const { value: targetDate } = await Swal.fire({
      title: "Trigger 24H Cashback Payout",
      text: "Enter cycle date (YYYY-MM-DD) or leave empty for yesterday:",
      input: "text",
      inputPlaceholder: "YYYY-MM-DD (e.g. 2026-08-14)",
      showCancelButton: true,
      confirmButtonText: "Calculate & Distribute",
    });

    if (targetDate !== undefined) {
      setTriggering(true);
      try {
        const res = await api.post("/cashback/trigger-payout", { date: targetDate || undefined });
        if (res.data?.success) {
          Swal.fire(
            "Completed",
            `Cashback calculated for ${res.data.cycleDate}. ${res.data.creditedCount} users credited total ${formatCurrency(res.data.totalCashbackDistributed)}.`,
            "success"
          );
          fetchSummary();
          fetchLogs();
        } else {
          Swal.fire("Error", res.data?.error || "Execution failed", "error");
        }
      } catch (err: any) {
        Swal.fire("Error", err?.response?.data?.message || "Trigger failed", "error");
      } finally {
        setTriggering(false);
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Title */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">5% Loss Cashback Management</h1>
          <p className="text-sm text-gray-500">
            Automated 24-hour net loss calculation and wallet cashback auto-credit.
          </p>
        </div>

        <button
          onClick={handleManualTrigger}
          disabled={triggering}
          className="px-4 py-2 bg-emerald-700 text-white rounded-xl font-medium text-sm hover:bg-emerald-800 transition disabled:opacity-50 flex items-center gap-2"
        >
          <span>⚡</span>
          <span>{triggering ? "Calculating..." : "Run 24H Payout Now"}</span>
        </button>
      </div>

      {/* Metrics Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Cashback Paid</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">
              {formatCurrency(summary.totals.total_cashback_distributed)}
            </p>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Net Losses Covered</p>
            <p className="text-2xl font-bold text-rose-600 mt-1">
              {formatCurrency(summary.totals.total_net_loss_covered)}
            </p>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Unique Users Rewarded</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {summary.totals.total_unique_users_rewarded}
            </p>
          </div>

          <div className="bg-white border rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Auto-Credit Cycle Status</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="h-3 w-3 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-base font-bold text-gray-800">
                {isActive ? "Active (Every 24H)" : "Paused"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Rate & Configuration Form */}
      <div className="bg-white border rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Cashback Rules Configuration</h2>

        <form onSubmit={handleSaveSettings} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
          <div>
            <label className="text-xs font-semibold text-gray-600">Cashback Rate (%)</label>
            <input
              type="number"
              step="0.1"
              value={rate}
              onChange={(e) => setRate(parseFloat(e.target.value))}
              className="w-full border rounded-lg px-3 py-2 mt-1 text-sm font-semibold"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600">Minimum Loss Threshold (৳)</label>
            <input
              type="number"
              step="1"
              value={minLoss}
              onChange={(e) => setMinLoss(parseFloat(e.target.value))}
              className="w-full border rounded-lg px-3 py-2 mt-1 text-sm font-semibold"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-emerald-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Enable Auto-Payouts</span>
            </label>

            <button
              type="submit"
              className="px-5 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition"
            >
              Save Settings
            </button>
          </div>
        </form>
      </div>

      {/* History Table */}
      <div className="bg-white border rounded-2xl shadow-sm p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-800">Cashback Payout History</h2>

          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search User ID..."
              value={searchUid}
              onChange={(e) => {
                setSearchUid(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-1.5 text-xs w-36"
            />

            <input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setPage(1);
              }}
              className="border rounded-lg px-3 py-1.5 text-xs"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Cycle Date</th>
                <th className="px-4 py-3 text-left">Total Bets</th>
                <th className="px-4 py-3 text-left">Total Wins</th>
                <th className="px-4 py-3 text-left">Net Loss</th>
                <th className="px-4 py-3 text-left">Rate</th>
                <th className="px-4 py-3 text-left">Cashback Paid</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-500">
                    Loading payout history...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-500">
                    No cashback records found.
                  </td>
                </tr>
              ) : (
                logs.map((row, i) => (
                  <tr key={row.id} className="border-t hover:bg-gray-50/80">
                    <td className="px-4 py-3 text-gray-500">{(page - 1) * 15 + i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">
                        {row.user_name || `UID: ${row.user_id}`}
                      </div>
                      <div className="text-xs text-gray-400">UID: {row.user_id}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{row.cycle_date}</td>
                    <td className="px-4 py-3 text-gray-600">{formatCurrency(row.total_bet)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatCurrency(row.total_win)}</td>
                    <td className="px-4 py-3 font-semibold text-rose-600">
                      {formatCurrency(row.net_loss)}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-600">{row.cashback_rate}%</td>
                    <td className="px-4 py-3 font-bold text-emerald-600">
                      {formatCurrency(row.cashback_amount)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center pt-4 border-t">
            <span className="text-xs text-gray-500">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 border rounded text-xs disabled:opacity-40"
              >
                Previous
              </button>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded text-xs disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashbackSettings;
